import { Toast as BaseToast } from '@base-ui/react/toast'
import { twMerge } from 'tailwind-merge'
import type { PartProps } from '../../../lib/part-props'

interface ToastRootProps extends PartProps<BaseToast.Root.Props> {
  className?: string
  /** Classes for the inner row that holds the icon, message and action. */
  contentClassName?: string
  /** A newer toast covers this one. It stays mounted with its timer running, hidden until the newer one goes. */
  covered?: boolean
}

/** Out the way it was swiped; a plain dismissal only fades. */
const SWIPE = [
  '[transform:translate(var(--toast-swipe-movement-x),var(--toast-swipe-movement-y))]',
  'data-[ending-style]:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)_+_150%))]',
  'data-[ending-style]:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)_+_150%))]',
].join(' ')

/**
 * One toast. Toasts replace each other in place: the newest fades in over the last, which stays mounted
 * underneath with its own timer and comes back if the newer one is dismissed before it expires. Hovering
 * pauses every timer. `dark` pins the semantic tokens to the dark ramp in both themes, so the surface, ink,
 * shadow and ghost button resolve dark. A 32px squircle rectangle, never a pill.
 */
function ToastRoot({ className, contentClassName, covered = false, children, ...props }: ToastRootProps) {
  return (
    <BaseToast.Root
      data-covered={covered ? '' : undefined}
      className={twMerge(
        'dark pointer-events-auto relative box-border w-max max-w-[min(480px,calc(100vw-32px))] select-none',
        // Every toast shares the viewport's one cell, newest on top.
        'z-[calc(1000-var(--toast-index))] [grid-area:1/1]',
        'corner-squircle bg-background-overlay text-foreground rounded-xl shadow-lg',
        SWIPE,
        'data-[ending-style]:opacity-0 data-[starting-style]:opacity-0',
        // Covered or past the limit: faded out, then invisible, so its action leaves the tab order.
        'data-[covered]:pointer-events-none data-[covered]:invisible data-[covered]:opacity-0',
        'data-[limited]:pointer-events-none data-[limited]:invisible data-[limited]:opacity-0',
        '[transition:opacity_150ms_cubic-bezier(0.22,1,0.36,1),visibility_0s_linear_0s,transform_0.3s_cubic-bezier(0.22,1,0.36,1)]',
        'data-[covered]:[transition:opacity_150ms_cubic-bezier(0.22,1,0.36,1),visibility_0s_linear_150ms]',
        'data-[limited]:[transition:opacity_150ms_cubic-bezier(0.22,1,0.36,1),visibility_0s_linear_150ms]',
        'motion-reduce:[transition:opacity_150ms]',
        className,
      )}
      {...props}
    >
      <BaseToast.Content
        className={twMerge(
          'flex min-h-8 items-center gap-2 px-3 py-1.5',
          // With an action the end padding drops to 4: the ghost button's own 8 is the inset the eye reads.
          'has-[[data-slot=toast-action]]:py-1 has-[[data-slot=toast-action]]:pe-1',
          // A glyph carries more air than a letter: start padding comes in by 2. The gap before an action stays 8.
          'has-[[data-slot=toast-icon]]:gap-1.5 has-[[data-slot=toast-icon]]:ps-2.5',
          'has-[[data-slot=toast-icon]]:[&>[data-slot=toast-action]]:ms-0.5',
          contentClassName,
        )}
      >
        {children}
      </BaseToast.Content>
    </BaseToast.Root>
  )
}

export type { ToastRootProps }
export { ToastRoot }
